// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe, isConnected } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

// Track shown notifications to prevent duplicates (especially in StrictMode)
const shownNotificationIds = new Set();

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);
  const subscribedRef = useRef(false);

  useEffect(() => {
    // Prevent double subscription in StrictMode
    if (subscribedRef.current) return;
    subscribedRef.current = true;
    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    // Show toast notification in the app
    function showToast(title, body, type = 'info') {
      const notifType = type === 'booking' ? 'success' : 
                        type === 'gift_card' ? 'info' : 'info';
      
      notification[notifType]({
        message: title,
        description: body,
        duration: 6,
        placement: 'topRight',
      });
    }

    function handleMessage(ev) {
      // ev.data is a JSON string from server
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      // Ignore pong messages (keepalive)
      if (msg?.type === "pong") return;
      
      // Also handle auth confirmation
      if (msg?.type === "auth" && msg?.ok) {
        console.log("[ws] authenticated as", msg?.user?.role);
        return;
      }

      const { event, payload } = msg || {};
      if (!event || !payload) return;

      console.log("[ws] received event:", event, payload);

      // Handle booking events
      if (event === "booking.created" || event === "booking.updated") {
        const notifId = payload._id || payload.id || `${event}_${Date.now()}`;
        
        // Skip if we've already shown this notification (prevents duplicates in StrictMode)
        if (shownNotificationIds.has(notifId)) {
          return;
        }
        shownNotificationIds.add(notifId);
        
        // Clean up old IDs after 30 seconds to prevent memory buildup
        setTimeout(() => shownNotificationIds.delete(notifId), 30000);
        
        playNotificationSound();
        
        // ✅ Push into notification list store
        addFromWs({ ...payload, event, type: 'booking' });
        
        // ✅ Show in-app toast notification
        showToast(payload.title || "New Booking", payload.body || "", 'booking');

        // ✅ Browser notification (requires permission)
        if (Notification?.permission === "granted") {
          new Notification(payload.title || "NextGen VR", {
            body: payload.body || "",
            icon: "/favicon.ico",
          });
        }
      }
      
      // Handle notification events (gift cards, etc.)
      if (event === "notification") {
        const notifId = payload.id || payload._id || `notif_${Date.now()}`;
        
        // Skip if we've already shown this notification
        if (shownNotificationIds.has(notifId)) {
          return;
        }
        shownNotificationIds.add(notifId);
        
        // Clean up old IDs after 30 seconds
        setTimeout(() => shownNotificationIds.delete(notifId), 30000);
        
        playNotificationSound();
        
        // payload contains: id, type, event as notifEvent, title, body, url, source, createdAt
        addFromWs({ 
          _id: payload.id || payload._id, 
          title: payload.title,
          body: payload.body,
          url: payload.url,
          source: payload.source,
          createdAt: payload.createdAt,
          event: payload.event, // the inner event like gift_card.purchased
          type: payload.type || 'gift_card',
        });

        // ✅ Show in-app toast notification
        showToast(payload.title || "Notification", payload.body || "", payload.type || 'gift_card');

        // ✅ Browser notification (requires permission)
        if (Notification?.permission === "granted") {
          new Notification(payload.title || "NextGen VR", {
            body: payload.body || "",
            icon: "/favicon.ico",
          });
        }
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      subscribedRef.current = false;
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
